import { Check, ArrowUpRight, FileText } from "lucide-react";
import { Reveal, Eyebrow, MaskLines } from "../landing/Reveal";
import { SmartLink } from "../landing/SmartLink";
import { AreasGrid } from "../landing/Areas";
import { REPORTS } from "../../content/pages";
import { DONATE_URL } from "../landing/data";
import { Stories } from "../landing/Stories";

const Wrap = ({ children, tone = "cream" }) => (
  <section className={`py-20 lg:py-28 ${tone === "sand" ? "bg-[#F3EADF]" : "bg-[#FAF7F2]"}`}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8">{children}</div>
  </section>
);

const Heading = ({ eyebrow, heading, light }) => (
  <>
    {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
    {heading && (
      <Reveal>
        <h2 className={`font-serif text-4xl sm:text-5xl mt-5 leading-[1] tracking-tight ${light ? "text-white" : "text-[#0F1D2F]"}`}>{heading}</h2>
      </Reveal>
    )}
  </>
);

export const PageHero = ({ eyebrow, title, intro, image }) => (
  <section data-testid="page-hero" className="relative bg-[#FAF7F2] pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
    <img src="/images/p5.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
    <div className="grain" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <Eyebrow testId="page-eyebrow">{eyebrow}</Eyebrow>
        <h1 data-testid="page-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#0F1D2F] mt-6 leading-[0.95] tracking-tight">
          <MaskLines lines={[title]} delay={0.1} />
        </h1>
        <Reveal delay={0.3}>
          <p data-testid="page-intro" className="mt-8 text-base sm:text-lg text-[#2B3A4A] leading-relaxed max-w-2xl">{intro}</p>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="lg:col-span-5">
        <div className="rounded-[200px_200px_28px_28px] overflow-hidden aspect-[4/3] lg:aspect-[4/5] max-h-[520px] w-full shadow-[0_40px_80px_-30px_rgba(179,76,10,0.45)]">
          <img src={image} alt={title} className="w-full h-full object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const CtaBtn = ({ cta, light }) =>
  cta ? (
    <SmartLink href={cta.href} data-testid={`cta-${cta.label.toLowerCase().replace(/\s/g, "-")}`} className={`${light ? "btn-fire" : "btn-dark"} mt-8`}>
      {cta.label} <ArrowUpRight className="w-4 h-4" />
    </SmartLink>
  ) : null;

const TextBlock = ({ eyebrow, heading, paras = [], image, reverse, checks, cta }) => (
  <Wrap tone={reverse ? "sand" : "cream"}>
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
        <Heading eyebrow={eyebrow} heading={heading} />
        {paras.map((p) => (
          <Reveal key={p.slice(0, 24)} delay={0.1}>
            <p className="mt-6 text-base sm:text-lg text-[#2B3A4A] leading-relaxed">{p}</p>
          </Reveal>
        ))}
        {checks && (
          <ul className="mt-8 space-y-3">
            {checks.map((c) => (
              <li key={c} className="flex gap-3 text-[#0F1D2F]">
                <span className="w-6 h-6 shrink-0 rounded-full bg-[#CF5C0F] text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></span>
                {c}
              </li>
            ))}
          </ul>
        )}
        <CtaBtn cta={cta} />
      </div>
      {image && (
        <Reveal delay={0.15} className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
          <div className="rounded-[28px] overflow-hidden aspect-[4/3] lg:aspect-[4/5]">
            <img src={image} alt={heading} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
          </div>
        </Reveal>
      )}
    </div>
  </Wrap>
);

const QuoteBlock = ({ eyebrow, heading, paras = [], cta }) => (
  <section className="bg-[#1B3824] py-20 lg:py-28 relative overflow-hidden">
    <img src="/images/wings.png" alt="" className="absolute -right-24 bottom-0 w-[460px] opacity-[0.08] pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
      <Heading eyebrow={eyebrow} heading={heading} light />
      {paras.map((p) => (
        <Reveal key={p.slice(0, 24)} delay={0.1}>
          <p className="mt-6 text-lg sm:text-xl text-[#E2E8E4] leading-relaxed">{p}</p>
        </Reveal>
      ))}
      <CtaBtn cta={cta} light />
    </div>
  </section>
);

const CardGrid = ({ items, numbered = true }) => (
  <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 md:auto-rows-fr">
    {items.map((it, i) => (
      <Reveal key={it.title} delay={(i % 3) * 0.08} className="h-full">
        <div data-testid={`info-card-${i}`} className="group h-full rounded-[28px] bg-white/80 border border-[#0F1D2F]/10 p-7 sm:p-8 hover:border-[#CF5C0F] hover:-translate-y-1 transition-[transform,border-color] duration-500">
          {numbered && <span className="font-mono text-sm text-[#B34C0A]">0{i + 1}</span>}
          <h3 className="font-serif text-2xl text-[#0F1D2F] mt-4 leading-tight">{it.title}</h3>
          <p className="mt-4 text-[#3A4756] leading-relaxed">{it.text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

const CardsBlock = ({ eyebrow, heading, items, intro }) => (
  <Wrap>
    <Heading eyebrow={eyebrow} heading={heading} />
    {intro && <p className="mt-6 max-w-3xl text-base sm:text-lg text-[#2B3A4A] leading-relaxed">{intro}</p>}
    <CardGrid items={items} />
  </Wrap>
);

const StatsBlock = ({ eyebrow, heading, items }) => (
  <section className="bg-[#0F1D2F] py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Heading eyebrow={eyebrow} heading={heading} light />
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-[28px] overflow-hidden">
        {items.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.08} className="bg-[#0F1D2F]">
            <div data-testid={`stat-${i}`} className="p-8 h-full">
              <p className="font-serif text-6xl text-fire">{s.value}</p>
              <p className="mt-4 text-white font-semibold text-lg">{s.title}</p>
              <p className="mt-2 text-[#CBD5E1] text-sm leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FeaturesBlock = ({ items }) => (
  <Wrap>
    <div className="space-y-20 lg:space-y-28">
      {items.map((f, i) => (
        <div key={f.title} data-testid={`activity-${i}`} className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
            <div className="rounded-[28px] overflow-hidden aspect-[16/10]">
              <img src={f.image} alt={f.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
            </div>
          </Reveal>
          <div className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
            <Eyebrow>{f.tag}</Eyebrow>
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#0F1D2F] mt-5 leading-[1]">{f.title}</h2>
              <p className="mt-6 text-base sm:text-lg text-[#2B3A4A] leading-relaxed">{f.text}</p>
            </Reveal>
          </div>
        </div>
      ))}
    </div>
  </Wrap>
);

const ReportsBlock = () => (
  <Wrap tone="sand">
    <Heading eyebrow="Information Hub" heading="Our latest publications" />
    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
      {REPORTS.map((r, i) => (
        <Reveal key={r.title} delay={i * 0.08} className="h-full">
          <div data-testid={`report-card-${i}`} className="h-full rounded-[28px] bg-white/80 border border-[#0F1D2F]/10 p-8 flex flex-col">
            <span className="w-12 h-12 rounded-2xl bg-[#F6E3D3] text-[#B34C0A] flex items-center justify-center"><FileText className="w-5 h-5" /></span>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#8A4B22] mt-8">{r.tag}</p>
            <h3 className="font-serif text-2xl text-[#0F1D2F] mt-2 leading-tight flex-1">{r.title}</h3>
            <p className="mt-6 text-sm text-[#5B6675]">[Report link to be added]</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Wrap>
);

const ProgramsBlock = () => (
  <Wrap tone="sand">
    <Heading eyebrow="Our Programs" heading="Our Areas of Work" />
    <div className="mt-12">
      <AreasGrid />
    </div>
  </Wrap>
);

export const SupportCta = () => (
  <section data-testid="support-cta" className="bg-[#CF5C0F] py-16 lg:py-20 relative overflow-hidden">
    <img src="/images/wings.png" alt="" className="absolute -left-20 -bottom-10 w-[380px] opacity-15 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
      <div className="max-w-2xl">
        <h2 className="font-serif text-4xl sm:text-5xl text-white leading-[1]">Support Our Efforts</h2>
        <p className="mt-4 text-white/90 text-base sm:text-lg">Donate today to support our community-based work. You can donate a set amount once or monthly. You can also choose to sponsor a child with a yearly donation of ₹15,000.</p>
      </div>
      <SmartLink href={DONATE_URL} data-testid="support-cta-donate" className="btn-dark btn-lg self-start lg:self-auto">
        Donate <ArrowUpRight className="w-5 h-5" />
      </SmartLink>
    </div>
  </section>
);

const BannerBlock = ({ image, caption }) => (
  <section className="bg-[#FAF7F2] pb-4">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <figure className="relative rounded-[28px] overflow-hidden aspect-[16/9]">
          <img src={image} alt={caption} className="w-full h-full object-cover" />
          <figcaption className="absolute left-6 bottom-6 font-serif italic text-xl sm:text-3xl text-[#0F1D2F] bg-[#FAF7F2]/85 backdrop-blur px-5 py-2 rounded-full">{caption}</figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const MAP = { stories: Stories, banner: BannerBlock, text: TextBlock, quote: QuoteBlock, cards: CardsBlock, levels: CardsBlock, stats: StatsBlock, features: FeaturesBlock, reports: ReportsBlock, programs: ProgramsBlock };

export const Block = ({ block }) => {
  const C = MAP[block.type];
  return C ? <C {...block} /> : null;
};
